import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { ComponentRegistry } from './ComponentRegistry';
import styles from './admin.module.css';

export interface Field {
  name: string; label: string; type?: 'text' | 'password' | 'number' | 'checkbox' | 'datetime-local' | 'textarea';
  options?: { value: string; label: string }[]; required?: boolean; min?: number; max?: number; step?: number | 'any'; maxLength?: number;
}
export interface Editor {
  title: string; path: string; method: 'POST' | 'PATCH'; fields: Field[]; values: Record<string, string | number | boolean>;
  transform?: (values: Record<string, string | number | boolean>) => unknown;
}
export function RecordEditor({ editor, busy, onSave, onCancel }: { editor: Editor; busy: boolean; onSave: (body: unknown) => Promise<void>; onCancel: () => void }): ReactElement {
  const [values, setValues] = useState(() => Object.fromEntries(editor.fields.map((field) => [
    field.name, field.type === 'number' ? String(editor.values[field.name] ?? '') : editor.values[field.name] ?? '',
  ])) as Record<string, string | number | boolean>);
  const [validationError, setValidationError] = useState('');
  async function submit(event: FormEvent) {
    event.preventDefault();
    const submittedValues = { ...values };
    const invalidNumber = editor.fields.find((field) => {
      if (field.type !== 'number') return false;
      const raw = values[field.name];
      if (raw === '' || (typeof raw === 'string' && raw.trim() === '')) return field.required === true;
      const number = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(number) || (field.min !== undefined && number < field.min) || (field.max !== undefined && number > field.max)) return true;
      if (typeof field.step === 'number') {
        const steps = (number - (field.min ?? 0)) / field.step;
        if (Math.abs(steps - Math.round(steps)) > 1e-8) return true;
      }
      submittedValues[field.name] = number;
      return false;
    });
    if (invalidNumber) {
      setValidationError(`请填写有效的${invalidNumber.label}。`);
      return;
    }
    setValidationError('');
    const body = editor.transform ? editor.transform(submittedValues) : submittedValues;
    // Remove the write-only field before any asynchronous operation or error display.
    if ('apiKey' in values) setValues((current) => ({ ...current, apiKey: '' }));
    await onSave(body);
  }
  return <ComponentRegistry id="admin.editor" className={styles.editor}>
    <h3>{editor.title}</h3>
    <form noValidate onSubmit={(event) => void submit(event)}>
      <fieldset disabled={busy} className={styles.fields}>
        {editor.fields.map((field) => {
          const inputId = `admin-edit-${field.name}`;
          const value = values[field.name] ?? '';
          if (field.type === 'checkbox') return <label className={styles.check} key={field.name}><input id={inputId} type="checkbox" checked={value === true} onChange={(event) => setValues({ ...values, [field.name]: event.target.checked })} />{field.label}</label>;
          return <label key={field.name} htmlFor={inputId} className={styles.field}><span>{field.label}</span>
            {field.options ? <select id={inputId} value={String(value)} required={field.required} onChange={(event) => setValues({ ...values, [field.name]: event.target.value })}>{field.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select> :
              field.type === 'textarea' ? <textarea id={inputId} value={String(value)} maxLength={field.maxLength} onChange={(event) => setValues({ ...values, [field.name]: event.target.value })} /> :
                <input id={inputId} type={field.type ?? 'text'} value={String(value)} required={field.required} min={field.min} max={field.max} step={field.step} maxLength={field.maxLength} autoComplete={field.type === 'password' ? 'new-password' : 'off'} onChange={(event) => setValues({ ...values, [field.name]: event.target.value })} />}
          </label>;
        })}
      </fieldset>
      <div className={styles.actions}><button type="submit" disabled={busy} className={styles.primary}>{busy ? '保存中…' : '保存'}</button><button type="button" disabled={busy} onClick={onCancel}>取消</button></div>
      {validationError && <p role="alert" className={styles.error}>{validationError}</p>}
    </form>
  </ComponentRegistry>;
}
